/**
 * ImportModal Component - Excel import modal for products and GTINs
 */
import React, { useState } from 'react';
import { Upload, X, Package, Barcode, FileSpreadsheet, CheckCircle, RefreshCw, ClipboardPaste } from 'lucide-react';
import { msg } from '../../utils/messages';
// N74 (Alana): mesma opção de colar da importação da base de contagem.
import { parseTsv, tsvToXlsxFile } from '../../utils/pasteToXlsx';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportProducts: (file: File) => Promise<any>;
  onImportGTINs: (file: File) => Promise<any>;
  /** N74: aba inicial. A tela Clientes > Produtos/GTIN tem um botão para cada
   *  catálogo, então abre o modal já na aba certa. Como o modal guarda estado
   *  interno, o chamador deve remontá-lo (`key`) ao trocar de aba. */
  initialTab?: 'produtos' | 'gtin';
}

export const ImportModal: React.FC<ImportModalProps> = ({
  isOpen,
  onClose,
  onImportProducts,
  onImportGTINs,
  initialTab = 'produtos',
}) => {
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  // N74: 'arquivo' (default, comportamento antigo) | 'colar'
  const [mode, setMode] = useState<'arquivo' | 'colar'>('arquivo');
  const [pastedText, setPastedText] = useState('');
  const [importStatus, setImportStatus] = useState<'idle' | 'uploading' | 'completed' | 'error'>('idle');
  const [importResult, setImportResult] = useState<any>(null);

  if (!isOpen) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setUploadedFile(file);
    } else {
      msg.error('Por favor, selecione um arquivo Excel (.xlsx ou .xls)');
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleImport = async () => {
    // N74: no modo "colar", o texto vira um .xlsx em memória e segue pelo MESMO
    // pipeline de upload (mesma validação do backend, nenhum endpoint novo).
    let fileToSend = uploadedFile;
    if (mode === 'colar') {
      fileToSend = tsvToXlsxFile(pastedText, `colado-${activeTab}.xlsx`);
      if (!fileToSend) {
        msg.error('Cole os dados do Excel primeiro');
        return;
      }
    } else if (!uploadedFile) {
      msg.error('Selecione um arquivo primeiro');
      return;
    }

    try {
      setImportStatus('uploading');

      if (activeTab === 'produtos') {
        const result = await onImportProducts(fileToSend!);
        // Map streaming NDJSON fields (total_processadas) to frontend fields
        const total = result.total_processadas || 0;
        const erros = result.total_erros || 0;
        const mappedResult = {
          ...result,
          produtos_criados: total,
          produtos_atualizados: 0,
          produtos_com_erro: erros,
        };
        setImportResult(mappedResult);
        if (total > 0) {
          msg.success(`${total} ${total === 1 ? 'produto importado' : 'produtos importados'}${erros > 0 ? ` (${erros} erros)` : ''}`);
        } else {
          msg.warning('Nenhum produto foi importado. Verifique o arquivo.');
        }
      } else {
        const result = await onImportGTINs(fileToSend!);
        // Map streaming NDJSON fields (total_processadas) to frontend fields
        const total = result.total_processadas || 0;
        const erros = result.total_erros || 0;
        const mappedResult = {
          ...result,
          gtins_criados: total,
          gtins_atualizados: 0,
          gtins_com_erro: erros,
        };
        setImportResult(mappedResult);
        if (total > 0) {
          msg.success(`${total} ${total === 1 ? 'GTIN importado' : 'GTINs importados'}${erros > 0 ? ` (${erros} erros)` : ''}`);
        } else {
          msg.warning('Nenhum GTIN foi importado. Verifique o arquivo.');
        }
      }

      setImportStatus('completed');
    } catch (error: any) {
      console.error('Erro ao importar:', error);
      setImportStatus('error');
      setImportResult(error.response?.data || { erros: [{ erro: 'Erro desconhecido ao importar' }] });
      msg.error('Erro ao importar arquivo. Verifique o formato.');
    }
  };

  const resetModal = () => {
    setUploadedFile(null);
    setPastedText('');
    setImportStatus('idle');
    setImportResult(null);
  };

  // N74: linhas coladas (para mostrar a contagem antes de importar)
  const pastedRows = mode === 'colar' ? parseTsv(pastedText).length : 0;
  const podeImportar = mode === 'colar' ? pastedRows > 0 : !!uploadedFile;

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 dark:bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-800">
        <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
          <h2 className="text-xl font-bold flex items-center gap-2 text-gray-900 dark:text-white">
            <Upload className="w-5 h-5 text-blue-600 dark:text-blue-500" />
            Importar Dados do Excel
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-800">
            <button
              onClick={() => {
                setActiveTab('produtos');
                resetModal();
              }}
              disabled={importStatus === 'uploading'}
              className={`px-4 py-3 font-medium text-sm transition-all flex items-center gap-2 border-b-2 disabled:opacity-50 ${
                activeTab === 'produtos'
                  ? 'text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-500'
                  : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-gray-300'
              }`}
            >
              <Package className="w-4 h-4" />
              Importar Produtos
            </button>
            <button
              onClick={() => {
                setActiveTab('gtin');
                resetModal();
              }}
              disabled={importStatus === 'uploading'}
              className={`px-4 py-3 font-medium text-sm transition-all flex items-center gap-2 border-b-2 disabled:opacity-50 ${
                activeTab === 'gtin'
                  ? 'text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-500'
                  : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-gray-300'
              }`}
            >
              <Barcode className="w-4 h-4" />
              Importar GTIN
            </button>
          </div>

          {/* Result Status */}
          {importStatus === 'completed' && importResult && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <h3 className="font-semibold text-green-800 dark:text-green-400 mb-2 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Importação Concluída!
              </h3>
              <div className="text-sm text-green-700 dark:text-green-300 space-y-1">
                {activeTab === 'produtos' ? (
                  <>
                    <p>✅ Produtos criados: {importResult.produtos_criados || 0}</p>
                    <p>🔄 Produtos atualizados: {importResult.produtos_atualizados || 0}</p>
                    {importResult.produtos_com_erro > 0 && (
                      <p className="text-orange-600 dark:text-orange-400">⚠️ Produtos com erro: {importResult.produtos_com_erro}</p>
                    )}
                  </>
                ) : (
                  <>
                    <p>✅ GTINs criados: {importResult.gtins_criados || 0}</p>
                    <p>🔄 GTINs atualizados: {importResult.gtins_atualizados || 0}</p>
                    {importResult.gtins_com_erro > 0 && (
                      <p className="text-orange-600 dark:text-orange-400">⚠️ GTINs com erro: {importResult.gtins_com_erro}</p>
                    )}
                  </>
                )}
              </div>
              {importResult.erros && importResult.erros.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto">
                  <p className="font-semibold text-red-700 dark:text-red-400 mb-1">Erros encontrados:</p>
                  {importResult.erros.map((erro: any, idx: number) => (
                    <p key={idx} className="text-xs text-red-600 dark:text-red-400">
                      Linha {erro.linha}: {erro.erro}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {importStatus === 'error' && importResult && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <h3 className="font-semibold text-red-800 dark:text-red-400 mb-2">Erro na Importação</h3>
              <p className="text-sm text-red-700 dark:text-red-300">
                {importResult.detail || importResult.erros?.[0]?.erro || 'Erro desconhecido'}
              </p>
            </div>
          )}

          {/* N74: escolha entre arquivo e colar do Excel */}
          {importStatus !== 'uploading' && (
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => { setMode('arquivo'); resetModal(); }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  mode === 'arquivo'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <FileSpreadsheet className="w-4 h-4" />
                Arquivo Excel
              </button>
              <button
                onClick={() => { setMode('colar'); resetModal(); }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  mode === 'colar'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <ClipboardPaste className="w-4 h-4" />
                Colar do Excel
              </button>
            </div>
          )}

          {/* N74: área de colar (TSV do Excel) */}
          {mode === 'colar' && importStatus !== 'uploading' && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Copie as células no Excel (Ctrl+C) e cole abaixo (Ctrl+V). Use as mesmas
                colunas do arquivo:{' '}
                <strong>
                  {activeTab === 'produtos'
                    ? 'código, descrição completa, unidade de medida'
                    : 'código, GTIN'}
                </strong>
                .
              </p>
              <textarea
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                rows={10}
                placeholder={
                  activeTab === 'produtos'
                    ? 'codigo\tdescricao_completa\tunidade_medida'
                    : 'codigo\tgtin'
                }
                className="w-full font-mono text-xs p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {pastedRows > 0
                  ? `${pastedRows} ${pastedRows === 1 ? 'linha colada' : 'linhas coladas'} (a primeira pode ser o cabeçalho)`
                  : 'Nenhuma linha colada ainda'}
              </p>
            </div>
          )}

          {/* Upload Area */}
          {mode === 'arquivo' && importStatus !== 'uploading' && (
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-700'
              }`}
            >
              <input
                type="file"
                id="file-upload"
                className="hidden"
                accept=".xlsx,.xls"
                onChange={handleFileInput}
              />

              {uploadedFile ? (
                <div className="space-y-4">
                  <FileSpreadsheet className="w-16 h-16 mx-auto text-green-600 dark:text-green-400" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{uploadedFile.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {(uploadedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <button
                    onClick={resetModal}
                    className="text-sm text-red-600 dark:text-red-400 hover:underline"
                  >
                    Remover arquivo
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Upload className="w-16 h-16 mx-auto text-gray-400" />
                  <div>
                    <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                      Arraste o arquivo Excel aqui
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      ou clique para selecionar
                    </p>
                    <label
                      htmlFor="file-upload"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Selecionar Arquivo
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Formatos aceitos: .xlsx, .xls
                  </p>
                </div>
              )}
            </div>
          )}

          {importStatus === 'uploading' && (
            <div className="flex flex-col items-center justify-center py-12">
              <RefreshCw className="w-16 h-16 text-blue-600 dark:text-blue-400 animate-spin mb-4" />
              <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Importando dados...
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Aguarde enquanto processamos o arquivo
              </p>
            </div>
          )}

          {/* Action Buttons */}
          {podeImportar && importStatus !== 'uploading' && (
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleImport}
                disabled={importStatus === 'uploading'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                <Upload className="w-4 h-4" />
                {importStatus === 'completed' ? 'Importar Novamente' : 'Importar'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
